/**
 * 埋点分析 SDK 封装（基于 ghost_sa 兼容服务端）
 *
 * 使用方式：
 *   import { analytics } from '@/src/utils/analytics'
 *   analytics.track('EventName', { prop: 'value' })
 */
import sensors from "sa-sdk-javascript";
import { UAParser } from "ua-parser-js";

const serverUrl = import.meta.env.VITE_SA_SERVER_URL as string | undefined;

if (serverUrl) {
  sensors.init({
    server_url: serverUrl,
    is_track_single_page: true, // SPA 路由变化自动采集 $pageview
    use_client_time: true,
    send_type: "image", // ghost_sa 推荐 image 方式上报
    show_log: import.meta.env.DEV || import.meta.env.VITE_DEPLOY_ENV === "test", // 开发 & 测试环境打印埋点日志
    heatmap: {
      clickmap: "not_collect", // 关闭点击图（ghost_sa 不支持可视化全埋点）
      scroll_notice_map: "default",
    },
  });

  // 解析 UA，注册为全局公共属性（每条事件自动携带）
  const ua = UAParser(navigator.userAgent);

  sensors.registerPage({
    $user_agent: navigator.userAgent,
    $os: ua.os.name ?? "",
    $os_version: ua.os.version ?? "",
    $browser: ua.browser.name ?? "",
    $browser_version: ua.browser.version ?? "",
    // device: 优先取设备型号，移动端有值，PC 端为 "desktop"
    $device:
      ua.device.model ??
      (ua.device.type === undefined ? "desktop" : (ua.device.type ?? "")),
  });

  sensors.quick("autoTrack"); // 自动采集 $pageview
}

export const analytics = sensors;

/**
 * 页面停留时长追踪
 *
 * 在页面组件挂载时调用，卸载时自动上报本次停留时长（ms）。
 * 停留时长低于 1 秒时不上报，以过滤 React StrictMode 双调用及页面瞬间切换产生的无效数据。
 * @param pageName 页面名称，如 "community"、"courses" 等
 * @returns cleanup 函数，在 useEffect return 中调用即可
 *
 * @example
 *   useEffect(() => trackPageDuration("community"), [])
 */
// 页面停留时长上报的最小阈值，过滤掉 React StrictMode 双调用及瞬间切换产生的无效数据
const MIN_DURATION_MS = 1000;

export function trackPageDuration(pageName: string): () => void {
  const startTime = Date.now();
  return () => {
    const duration = Date.now() - startTime;
    if (duration < MIN_DURATION_MS) return;
    analytics.track("PageDuration", {
      page_name: pageName,
      duration_ms: duration,
      duration_s: Math.round(duration / 1000),
    });
  };
}
